// Client-side PDF generator for the Pending Works statement.
// Uses jsPDF + jspdf-autotable to produce a compact, branded A4 PDF that
// mirrors the on-screen print statement. Returns the jsPDF instance so the
// caller can .save() it or grab a Blob for sharing.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const INR = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDeadline = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
};

export function buildPendingWorksPdf({ company = {}, store = {}, tab = "pending", rows = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const w = doc.internal.pageSize.getWidth();

  // Header block
  const compName = company.name || "Company";
  const storeName = store?.name ? ` · ${store.name}` : "";
  doc.setFont("helvetica", "bold").setFontSize(16).text(`${compName}${storeName}`, 40, 46);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(80)
    .text(company.address || "", 40, 62);
  if (company.gstin) doc.text(`GSTIN: ${company.gstin}`, 40, 76);

  doc.setDrawColor(220).line(40, 84, w - 40, 84);
  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(0)
    .text(`Daily Pending Works — ${tab.charAt(0).toUpperCase() + tab.slice(1)}`, 40, 106);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(100)
    .text(`Generated ${new Date().toLocaleString("en-IN")} · ${rows.length} task(s)`, 40, 122);

  // Table
  const body = rows.map((r) => {
    const item = (r.lines && r.lines.length)
      ? r.lines.map((ln) => `${ln.item_name || "-"} x ${ln.quantity || 1}`).join("\n")
      : (r.item_name || "-");
    const contact = [r.customer_phone, r.customer_email].filter(Boolean).join("\n");
    return [
      r.customer_name || "-",
      contact || "-",
      item,
      fmtDeadline(r.deadline),
      r.amount ? INR(r.amount) : "-",
    ];
  });

  const totalAmt = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  autoTable(doc, {
    startY: 140,
    head: [["Party", "Contact", "Items", "Deadline", "Amount"]],
    body,
    styles: { fontSize: 9, cellPadding: 6, valign: "top", overflow: "linebreak" },
    headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 110, fontStyle: "bold" },
      1: { cellWidth: 110 },
      2: { cellWidth: 160 },
      3: { cellWidth: 90 },
      4: { cellWidth: 70, halign: "right" },
    },
    foot: [[
      { content: `Total tasks: ${rows.length}`, colSpan: 4, styles: { halign: "right", fontStyle: "bold" } },
      { content: INR(totalAmt), styles: { halign: "right", fontStyle: "bold" } },
    ]],
    footStyles: { fillColor: [241, 245, 249], textColor: 0 },
    didDrawPage: (data) => {
      // Page footer
      const pageCount = doc.internal.getNumberOfPages();
      const pageNum = data.pageNumber;
      const h = doc.internal.pageSize.getHeight();
      doc.setFontSize(8).setTextColor(140);
      doc.text(`Vetrich ERP · Page ${pageNum} of ${pageCount}`, w / 2, h - 20, { align: "center" });
    },
  });
  return doc;
}
